import { z } from 'zod';

const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/;
const colour = z.string().regex(HEX_COLOUR, 'Use a six-digit hex colour, for example #5F51E8');

/**
 * A value that is not supplied is null, not ''. The form submits '' for a cleared
 * input, so it is normalised rather than rejected — which is what lets a value be
 * cleared.
 */
function optionalText() {
  return z.union([z.string(), z.null()]).transform((value) => {
    // Trimmed before the empty check: a field holding only spaces is not a value
    // an email can render, but it is not null either, so the footer printed the
    // spaces instead of leaving the line out.
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
  });
}

/**
 * z.url() alone accepts `javascript:` and `data:`, and these values are rendered
 * as link targets in an email and as an image source, so the scheme is restricted.
 *
 * https only, not http: a logo fetched over http is mixed content in the clients
 * that render the email over https, and a plain-http link in mail we send is a
 * deliverability and trust problem rather than merely an untidy one.
 */
function optionalUrl() {
  return z
    .union([
      z.url({ protocol: /^https$/, error: 'Enter a full address starting with https://' }),
      z.literal(''),
      z.null(),
    ])
    .transform((value) => (value === '' || value == null ? null : value));
}

function optionalEmail() {
  return z
    .union([
      z
        .string()
        .trim()
        .pipe(z.union([z.email({ error: 'Enter a valid email address' }), z.literal('')])),
      z.null(),
    ])
    .transform((value) => (value === '' || value == null ? null : value));
}

/**
 * The organisation's identity. Filed under identity rather than email branding
 * because the RTO code and support contact are the same ones the student app and
 * the AVETMISS export use.
 *
 * Apart from the router so the admin app can share it without pulling a database
 * client into the browser bundle.
 */
export const OrganisationIdentitySchema = z.object({
  // Always present: an organisation is named when it is registered.
  organisationName: z.string().min(1, 'Organisation name is required'),

  legalName: optionalText(),
  rtoCode: optionalText(),
  supportEmail: optionalEmail(),
  privacyEmail: optionalEmail(),
  complaintsEmail: optionalEmail(),
  workPlacementEmail: optionalEmail(),
  supportPhoneVanity: optionalText(),
  supportPhoneDigits: optionalText(),
  contactUrl: optionalUrl(),
  privacyNoticeUrl: optionalUrl(),
  logoUrl: optionalUrl(),
  logoGreyscaleUrl: optionalUrl(),

  // Never unknown: provisioning writes a default that renders correctly.
  linkColour: colour,
  buttonBackgroundColour: colour,
  buttonTextColour: colour,
});

/**
 * A save carries only the fields that were edited: absent means untouched, null
 * means cleared. Without that, saving would write back every value the form
 * loaded, reverting anything another admin had changed in the meantime.
 */
export const OrganisationIdentityUpdateSchema = OrganisationIdentitySchema.partial();

export type OrganisationIdentityUpdate = z.infer<typeof OrganisationIdentityUpdateSchema>;

/** What the procedures read and write: an unset field is null. */
export type OrganisationIdentity = z.infer<typeof OrganisationIdentitySchema>;

/** What a form may submit: '' for cleared, absent for a field it does not own. */
export type OrganisationIdentityFormValues = z.input<typeof OrganisationIdentityUpdateSchema>;
